import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  Mail,
  PlugZap,
  RefreshCw,
  Save,
  Send,
  Webhook,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime, fmtMoney } from "@/lib/intl";

const ORIGIN =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

export default function InvoiceSyncSettings() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [accountCode, setAccountCode] = useState("NoCode83");
  // Daily Xero CSV email
  const [csvEmailEnabled, setCsvEmailEnabled] = useState(false);
  const [csvEmailRecipient, setCsvEmailRecipient] = useState("");
  const [csvEmailCc, setCsvEmailCc] = useState("");
  const [trackingName1, setTrackingName1] = useState("");
  const [trackingOption1, setTrackingOption1] = useState("");
  const [trackingName2, setTrackingName2] = useState("");
  const [trackingOption2, setTrackingOption2] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get(`/invoice-sync/config`, { params: { origin: ORIGIN } }),
        api.get(`/invoice-sync/log`, { params: { limit: 50 } }),
      ]);
      setCfg(c.data);
      setEnabled(!!c.data.enabled);
      setAccountCode(c.data.account_code || "NoCode83");
      setCsvEmailEnabled(!!c.data.csv_email_enabled);
      setCsvEmailRecipient(c.data.csv_email_recipient || "");
      setCsvEmailCc(c.data.csv_email_cc || "");
      setTrackingName1(c.data.tracking_name_1 || "");
      setTrackingOption1(c.data.tracking_option_1 || "");
      setTrackingName2(c.data.tracking_name_2 || "");
      setTrackingOption2(c.data.tracking_option_2 || "");
      setLog(l.data || []);
    } catch (e) {
      // best-effort; surface only on save/run
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.post("/invoice-sync/config", {
        enabled,
        account_code: accountCode.trim() || "NoCode83",
        tax_type: cfg?.tax_type || "OUTPUT",
        csv_email_enabled: csvEmailEnabled,
        csv_email_recipient: csvEmailRecipient.trim() || null,
        csv_email_cc: csvEmailCc.trim() || null,
        tracking_name_1: trackingName1.trim() || null,
        tracking_option_1: trackingOption1.trim() || null,
        tracking_name_2: trackingName2.trim() || null,
        tracking_option_2: trackingOption2.trim() || null,
      });
      setCfg((prev) => ({ ...(prev || {}), ...r.data }));
      toast.success("Invoice-sync settings saved");
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function sendCsvEmailNow() {
    if (!csvEmailRecipient.trim()) {
      toast.error("Please set a bookkeeper recipient first");
      return;
    }
    setEmailing(true);
    try {
      const r = await api.post("/invoice-sync/email-csv-now", null, { timeout: 60000 });
      toast.success(`Sent · ${r.data?.included ?? 0} order(s) included`);
      await refresh();
    } catch (e) {
      toast.error("Send failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setEmailing(false);
    }
  }

  async function registerWebhook() {
    setRegistering(true);
    try {
      const r = await api.post(
        "/invoice-sync/register-webhook",
        null,
        { params: { origin: ORIGIN } }
      );
      toast.success("Webhook registered with Acuity", {
        description: r.data?.target_url,
      });
      await refresh();
    } catch (e) {
      toast.error("Register failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRegistering(false);
    }
  }

  async function unregisterWebhook() {
    setRegistering(true);
    try {
      await api.delete("/invoice-sync/webhook");
      toast.success("Webhook removed from Acuity");
      await refresh();
    } catch (e) {
      toast.error("Unregister failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRegistering(false);
    }
  }

  async function refreshLog() {
    setLogLoading(true);
    try {
      const r = await api.get("/invoice-sync/log", { params: { limit: 50 } });
      setLog(r.data || []);
    } finally {
      setLogLoading(false);
    }
  }

  const webhookRegistered = !!cfg?.webhook_registered_id;
  const lastEvent = cfg?.last_event_summary;

  return (
    <Card
      className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none"
      data-testid="invoice-sync-settings"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <FileText className="h-3.5 w-3.5" /> Acuity → Xero invoices
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-zinc-600">
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="invoice-sync-enabled-switch"
          />
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-600 max-w-3xl leading-relaxed">
        Every time a customer completes a product order in Acuity, an
        <strong> Authorised, tax-inclusive </strong> invoice is created in Xero
        against the matching contact (auto-created by email if needed). Real-time
        via webhook.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-700">Xero account code</Label>
          <Input
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            placeholder="NoCode83"
            className="mt-1.5 bg-white border-zinc-300 font-mono-num focus-visible:ring-zinc-950"
            data-testid="invoice-sync-account-code"
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            All product lines post to this code in your chart of accounts.
          </p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-zinc-700">Webhook target (read-only)</Label>
          <Input
            readOnly
            value={cfg?.webhook_target_url || ""}
            className="mt-1.5 bg-zinc-50 border-zinc-300 font-mono-num text-xs"
            data-testid="invoice-sync-webhook-target"
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Acuity will POST <code>order.completed</code> events here. Signed
            with your API key.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="invoice-sync-save-btn"
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save settings"}
        </Button>
        {webhookRegistered ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 h-9">
              <CheckCircle2 className="h-3.5 w-3.5" /> Webhook registered ·{" "}
              <span className="font-mono-num">#{cfg.webhook_registered_id}</span>
            </span>
            <Button
              onClick={unregisterWebhook}
              disabled={registering}
              variant="outline"
              data-testid="invoice-sync-unregister-webhook-btn"
              className="border-zinc-300 hover:bg-zinc-100"
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              {registering ? "…" : "Remove webhook"}
            </Button>
          </>
        ) : (
          <Button
            onClick={registerWebhook}
            disabled={registering}
            variant="outline"
            data-testid="invoice-sync-register-webhook-btn"
            className="border-zinc-300 hover:bg-zinc-100"
          >
            <Webhook className="h-4 w-4 mr-1.5" />
            {registering ? "Registering…" : "Register webhook in Acuity"}
          </Button>
        )}
      </div>

      {lastEvent && (
        <div className="mt-5 text-xs text-zinc-500 font-mono-num">
          Last event:{" "}
          <span className="text-zinc-700">
            {lastEvent.action} (id {lastEvent.id})
          </span>
          {cfg?.last_event_at_iso && (
            <>
              {" "}· {fmtDateTime(cfg.last_event_at_iso)}
            </>
          )}
        </div>
      )}

      <div
        className="mt-6 rounded-md border border-zinc-200 bg-white p-4 sm:p-5"
        data-testid="invoice-sync-tracking-section"
      >
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600 mb-1">
          Xero tracking categories (optional)
        </div>
        <p className="text-xs text-zinc-600 max-w-3xl mb-4">
          Tag every invoice line with up to two tracking categories from Xero
          (Accounting → Advanced → Tracking categories). Both the category
          name AND the option name must match Xero exactly, or the CSV import
          will fail. Leave blank to skip.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-700 text-xs">Tracking name 1 (category)</Label>
            <Input
              value={trackingName1}
              onChange={(e) => setTrackingName1(e.target.value)}
              placeholder="e.g. Account Type"
              className="mt-1.5 bg-white border-zinc-300 font-mono-num"
              data-testid="invoice-sync-tracking-name-1"
            />
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">Tracking option 1 (value)</Label>
            <Input
              value={trackingOption1}
              onChange={(e) => setTrackingOption1(e.target.value)}
              placeholder="e.g. Accounts Receivable"
              className="mt-1.5 bg-white border-zinc-300 font-mono-num"
              data-testid="invoice-sync-tracking-option-1"
            />
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">Tracking name 2 (optional)</Label>
            <Input
              value={trackingName2}
              onChange={(e) => setTrackingName2(e.target.value)}
              className="mt-1.5 bg-white border-zinc-300 font-mono-num"
              data-testid="invoice-sync-tracking-name-2"
            />
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">Tracking option 2 (optional)</Label>
            <Input
              value={trackingOption2}
              onChange={(e) => setTrackingOption2(e.target.value)}
              className="mt-1.5 bg-white border-zinc-300 font-mono-num"
              data-testid="invoice-sync-tracking-option-2"
            />
          </div>
        </div>
      </div>

      <div
        className="mt-8 rounded-md border border-zinc-200 bg-zinc-50 p-4 sm:p-5"
        data-testid="invoice-sync-csv-email-section"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600">
            <Mail className="h-3.5 w-3.5" /> Daily Xero CSV email
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-600">
              {csvEmailEnabled ? "Enabled" : "Disabled"}
            </span>
            <Switch
              checked={csvEmailEnabled}
              onCheckedChange={setCsvEmailEnabled}
              data-testid="invoice-sync-csv-email-enabled"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-zinc-600 max-w-3xl">
          At <strong>6:00 AM Australia/Melbourne</strong> each day, email yesterday's
          Xero invoice CSV (excluding orders already pushed via the API) as an
          attachment so your bookkeeper can import it. Uses the same Resend API
          key as the daily schedule email.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-700 text-xs">Bookkeeper email (To)</Label>
            <Input
              value={csvEmailRecipient}
              onChange={(e) => setCsvEmailRecipient(e.target.value)}
              placeholder="bookkeeper@example.com"
              className="mt-1.5 bg-white border-zinc-300"
              data-testid="invoice-sync-csv-recipient"
            />
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">CC (optional)</Label>
            <Input
              value={csvEmailCc}
              onChange={(e) => setCsvEmailCc(e.target.value)}
              placeholder="you@example.com, partner@example.com"
              className="mt-1.5 bg-white border-zinc-300"
              data-testid="invoice-sync-csv-cc"
            />
            <p className="mt-1 text-2xs text-zinc-500">
              Comma-separated. Save the section first, then test with "Send now".
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            onClick={sendCsvEmailNow}
            disabled={emailing || !csvEmailRecipient.trim()}
            variant="outline"
            data-testid="invoice-sync-csv-send-now"
            className="border-zinc-300 hover:bg-zinc-100"
            size="sm"
          >
            <Send className={`h-4 w-4 mr-1.5 ${emailing ? "animate-pulse" : ""}`} />
            {emailing ? "Sending…" : "Send yesterday's CSV now"}
          </Button>
          {cfg?.last_csv_email_at_iso && (
            <span className="text-2xs text-zinc-500 font-mono-num">
              Last sent: {fmtDateTime(cfg.last_csv_email_at_iso)}
              {cfg?.last_csv_email_summary?.included != null
                ? ` · ${cfg.last_csv_email_summary.included} order(s)`
                : ""}
            </span>
          )}
        </div>
        {cfg?.last_csv_email_summary?.error && (
          <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
            Last send error: {cfg.last_csv_email_summary.error}
          </div>
        )}
      </div>

      <div className="mt-6 rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="invoice-sync-log">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Recent invoices
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshLog}
            data-testid="invoice-sync-refresh-log"
            className="h-7 -mr-2 px-2 text-zinc-500 hover:text-zinc-900"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${logLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {log.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-zinc-500">
            No orders pushed yet. New Acuity orders will appear here once the
            webhook fires.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  When
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  Order
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  Customer
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600 text-right">
                  Total
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  Xero
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.map((row) => {
                const snap = row.order_snapshot || {};
                const name =
                  `${(snap.firstName || "").trim()} ${(snap.lastName || "").trim()}`.trim() ||
                  snap.email ||
                  "—";
                return (
                  <TableRow key={row.acuity_order_id} data-testid={`invoice-row-${row.acuity_order_id}`}>
                    <TableCell className="font-mono-num text-xs text-zinc-600">
                      {row.updated_at_iso ? fmtDateTime(row.updated_at_iso) : "—"}
                    </TableCell>
                    <TableCell className="font-mono-num text-xs">
                      #{row.acuity_order_id}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{name}</div>
                      <div className="text-2xs text-zinc-500">{snap.email}</div>
                    </TableCell>
                    <TableCell className="font-mono-num text-sm text-right">
                      {fmtMoney(row.total)}
                    </TableCell>
                    <TableCell>
                      {row.status === "ok" ? (
                        <a
                          href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${row.invoice_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"
                        >
                          {row.invoice_number || "View"}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span
                          className="text-xs text-rose-700"
                          title={row.error || ""}
                        >
                          {row.status}
                          {row.error ? " — " : ""}
                          <span className="text-zinc-500">
                            {(row.error || "").slice(0, 60)}
                          </span>
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {!enabled && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900 px-3 py-2 flex items-center gap-2">
          <PlugZap className="h-3.5 w-3.5" />
          Sync is currently <strong>disabled</strong> — webhooks will be received
          and logged but no invoice will be created.
        </div>
      )}
    </Card>
  );
}
